<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y" v-if="training">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('trainings.timeline.title') }}</h4>
					<p class="mb-0">{{ $t('trainings.timeline.subtitle') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button class="btn btn-primary waves-effect waves-light" type="button" disabled="" v-if="isDownloading">
						<span class="spinner-border me-1" role="status" aria-hidden="true"></span>
						{{ $t('common.loading') }}
					</button>
					<button type="submit" class="btn btn-primary" @click="downloadEvents()" v-if="!isDownloading">
						{{ $t('trainings.timeline.downloadEvents') }}
					</button>
				</div>
			</div>

			<div class="col-md-12 mb-5">
				<div class="card h-100">
					<div class="card-body pb-4">
						<div class="mb-0">
							<div class="d-flex align-items-center">
								<div class="avatar avatar-md">
									<div class="avatar-initial bg-label-primary rounded-3">
										<i class="ri-time-line ri-24px"></i>
									</div>
								</div>
								<div class="ms-4">
									<p class="mb-0">
										<i18n-t keypath="trainings.timeline.totalTimeSpent" tag="span">
												<template #name>{{ training.firstName }}</template>
												<template #time><strong>{{ totalTimeSpent }}</strong></template>
											</i18n-t>
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card mb-6">
				<h5 class="card-header">{{ $t('trainings.timeline.trackingTitle', { name: training.firstName }) }}</h5>
				<div class="card-body pt-0">
					<ul class="timeline mb-0 mt-2">
						<li class="timeline-item timeline-item-transparent" v-for="event in events" :key="event._id">
							<span :class="['timeline-point', getTimelinePointClass(event.type)]"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0" v-if="event.type === 'cancelled' || event.type === 'sent-training-reminder'">
										{{ getEventTitle(event) }}
									</h6>
									<h6 class="mb-0" v-else>
										{{ training.firstName }} {{ getEventTitle(event) }}
									</h6>
									<small class="text-muted">{{ formatDate(event.createdAt) }}</small>
								</div>
								<p class="mb-2" v-if="getEventDetails(event)">
									{{ getEventDetails(event) }}
								</p>
							</div>
						</li>
						<li class="timeline-item timeline-item-transparent" v-if="training.startedAt">
							<span class="timeline-point timeline-point-success"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0">
										{{ $t('trainings.timeline.startedTraining', { name: training.firstName, formation: training.formationName }) }}
									</h6>
									<small class="text-muted">{{ formatDate(training.startedAt) }}</small>
								</div>
								<p class="mb-2">{{ $t('trainings.timeline.clickedLink') }}</p>
							</div>
						</li>
						<li class="timeline-item timeline-item-transparent">
							<span class="timeline-point timeline-point-success"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0">{{ $t('trainings.timeline.registeredTraining', { formation: training.formationName }) }}</h6>
									<small class="text-muted">{{ formatDate(training.createdAt) }}</small>
								</div>
								<p class="mb-2">{{ $t('trainings.timeline.invitationSent', { email: training.email }) }}</p>
							</div>
						</li>
					</ul>
				</div>
			</div>
			<div
				class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6">
				<div class="d-flex align-content-center flex-wrap gap-4">
					<router-link to="/trainings/list">
						<button class="btn btn-outline-secondary">{{ $t('common.actions.back') }}</button>
					</router-link>
				</div>
			</div>
		</div>
		<!-- / Content -->
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import i18n from "@/i18n";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "TrainingTimeline",

	data() {
		return {
			traineeId: null,
			isDownloading: false,
			totalTimeSpent: null,
			training: null,
			events: [],
		};
	},

	methods: {
		getTimelinePointClass(eventType) {
			const classes = {
				"viewed-video": "timeline-point-primary",
				"started-interaction": "timeline-point-secondary",
				"finished-interaction": "timeline-point-success",
				"replied-to-question": "timeline-point-warning",
				"next-chapter": "timeline-point-success",
				"failed-quiz": "timeline-point-danger",
				"validated-quiz": "timeline-point-success",
				"login-with-code": "timeline-point-success",
				"submitted-evaluation-code": "timeline-point-success",
				"viewed-genially-slide": "timeline-point-primary",
				"viewed-player-slide": "timeline-point-primary",
				"answered-player-question": "timeline-point-info",
				"player-error": "timeline-point-danger",
				"cancelled": "timeline-point-danger",
				"sent-training-reminder": "timeline-point-info",
				"finished-initial-evaluation": "timeline-point-primary",
				default: "timeline-point-secondary"
			};
			return classes[eventType] || classes.default;
		},

		getEventTitle(event) {
			if (event.type === 'sent-training-reminder') {
				return this.$t(event.metadata?.delivery === 'local' ? 'trainings.timeline.events.reminderSimulated' : 'trainings.timeline.events.reminderSent', {
					manager: event.metadata?.managerName || this.$t('common.roles.manager'),
					channel: event.metadata?.channel === 'sms' ? 'SMS' : this.$t('trainings.inactivity.reminder.emailChannel'),
				});
			}
			const titles = {
				"viewed-video": this.$t('trainings.timeline.events.viewedVideo'),
				"started-interaction": this.$t('trainings.timeline.events.startedInteraction'),
				"finished-interaction": this.$t('trainings.timeline.events.finishedInteraction'),
				"replied-to-question": this.$t('trainings.timeline.events.repliedToQuestion'),
				"next-chapter": this.$t('trainings.timeline.events.nextChapter'),
				"failed-quiz": this.$t('trainings.timeline.events.failedQuiz', { score: event.metadata?.finalScore }),
				"validated-quiz": this.$t('trainings.timeline.events.validatedQuiz', { score: event.metadata?.finalScore }),
				"login-with-code": this.$t('trainings.timeline.events.loginWithCode'),
				"submitted-evaluation-code": this.$t('trainings.timeline.events.submittedEvalCode', { code: event.metadata?.code }),
				"viewed-player-slide": this.$t('trainings.timeline.events.viewedGeniallySlide', { slideNumber: (event.metadata?.toIndex ?? 0) + 1 }),
				"answered-player-question": this.$t('trainings.timeline.events.answeredPlayerQuestion'),
				"player-error": this.$t('trainings.timeline.events.playerError'),
				"viewed-genially-slide": this.$t('trainings.timeline.events.viewedGeniallySlide', { slideNumber: event.metadata?.index }),
				"cancelled": this.$t('trainings.timeline.events.cancelled', { manager: event.metadata?.managerName }),
				"finished-initial-evaluation": this.$t('trainings.timeline.events.finishedInitialEval', { score: event.metadata?.finalScore }),
				default: this.$t('trainings.timeline.events.unknown')
			};
			return titles[event.type] || titles.default;
		},

		getEventDetails(event) {
			const parts = [];
			if (event.customerUserAgent?.browser) parts.push(event.customerUserAgent.browser);
			if (event.customerUserAgent?.platform) parts.push(event.customerUserAgent.platform);
			if (event.ipData) parts.push(this.formatLocation(event.ipData));
			else if (event.customerIp) parts.push(event.customerIp);
			return parts.join(" • ");
		},

		async fetchTrainingEvents(traineeId) {
			try {
				// Appel API avec les filtres et pagination
				const response = await HTTP.get(`/trainings/${traineeId}/timeline`);

				// Mise à jour des données
				this.totalTimeSpent = response.data.totalTimeSpent;
				this.training = response.data.training;

				this.events = response.data.events.map(ev => {
					if (ev.metadata?.finalScore) {
						ev.metadata.finalScore = (typeof ev.metadata.finalScore === "number" ? ev.metadata.finalScore.toFixed(2) : ev.metadata.finalScore)
					}
					return ev
				});
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message || i18n.global.t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadEvents() {
			try {
				this.isDownloading = true;

				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/trainings/${this.traineeId}/download-timeline`, { responseType: 'blob' });

				if (response.data) {
					// response.data est déjà un Blob CSV
					const blob = new Blob([response.data], { type: 'text/csv' });
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `training_${this.traineeId}_events.csv`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					this.isDownloading = false;
				} else {
					throw new Error(this.$t('trainings.timeline.fileUnavailable'));
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message ||
					i18n.global.t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		formatRelativeDate(date) {
			return moment(date).fromNow(); // Affiche "il y a 3 semaines"
		},

		formatDate(date) {
			return moment(date).format("llll"); // Affiche "il y a 3 semaines"
		},
	},

	computed: {
		formatLocation() {
			return (ipData) => {
				if (!ipData) return null;
				if (ipData.region && ipData.city) return `${ipData.region} (${ipData.city})`;
				if (ipData.region) return ipData.region;
				if (ipData.city) return ipData.city;
				return this.$t('trainings.timeline.unknownLocation');
			};
		}
	},

	async mounted() {
		this.traineeId = this.$route.params.traineeId;
		await this.fetchTrainingEvents(this.traineeId);
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}
</style>
